import {
  SOCIAL_PLATFORMS,
  SOCIAL_POST_SOURCES,
  SOCIAL_SENTIMENTS,
  SocialPlatform,
  SocialPostSource,
  SocialSentiment,
} from '@digital-twins/shared-types'
import type { AssistantSocialFocus } from '@digital-twins/shared-types/assistant'
import { socialWindowFromParam } from '@/pages/KnowledgeHubPage/social/socialFormat'
import { ROUTES } from '@/routes/routes'

import { KnowledgeScopeSelection } from './KnowledgeScopePicker'

/** The query param that scopes an ask to one Library collection (D4). */
export const LIBRARY_COLLECTION_SCOPE_PARAM = 'libraryCollection'

/** Params a Topic Ask link carries (plan 2026-11-01 D1); `window` is shared with the tracker links. */
const SOCIAL_FOCUS_PARAMS = ['topic', 'tracker', 'post', 'theme', 'brand', 'platform', 'sentiment']

/** Params an "Ask about this" link carries; Home and the Threads lobby both read them. */
const ASK_SCOPE_PARAMS = [
  LIBRARY_COLLECTION_SCOPE_PARAM,
  'doc',
  'collection',
  'source',
  'window',
  ...SOCIAL_FOCUS_PARAMS,
]

/** Most posts one focus pins (D1). */
const SOCIAL_FOCUS_POSTS_MAX = 5

function includes<T extends string>(values: ReadonlyArray<T>, value: string | null): value is T {
  return value != null && (values as ReadonlyArray<string>).includes(value)
}

function paramValue(params: URLSearchParams, key: string): string | null {
  const value = params.get(key)?.trim() ?? ''
  return value.length > 0 ? value : null
}

/**
 * The Topic focus a `topic` link names, or null without one: the Topic, and
 * the tracker, posts, theme, brand and filters the user asked from. A value
 * outside the contract reads as unset.
 */
export function socialFocusFromParams(params: URLSearchParams): AssistantSocialFocus | null {
  const topicToken = paramValue(params, 'topic')
  if (!topicToken) {
    return null
  }
  const postTokens = [
    ...new Set(params.getAll('post').map((token) => token.trim()).filter(Boolean)),
  ].slice(0, SOCIAL_FOCUS_POSTS_MAX)
  const platform = params.get('platform')
  const sentiment = params.get('sentiment')
  const source = params.get('source')
  const window = socialWindowFromParam(params.get('window'))
  const filters = {
    ...(includes<SocialPlatform>(SOCIAL_PLATFORMS, platform) ? { platform } : {}),
    ...(includes<SocialSentiment>(SOCIAL_SENTIMENTS, sentiment) ? { sentiment } : {}),
    ...(includes<SocialPostSource>(SOCIAL_POST_SOURCES, source) ? { source } : {}),
    ...(window ? { from: window.from, to: window.to } : {}),
  }
  const trackerToken = paramValue(params, 'tracker')
  const themeId = paramValue(params, 'theme')
  const brandId = paramValue(params, 'brand')
  return {
    topicToken,
    ...(trackerToken ? { trackerToken } : {}),
    ...(postTokens.length > 0 ? { postTokens } : {}),
    ...(themeId ? { themeId } : {}),
    ...(brandId ? { brandId } : {}),
    ...(Object.keys(filters).length > 0 ? { filters } : {}),
  }
}

/** Home with the composer scoped to one Library collection, ready to ask. */
export function askLibraryCollectionPath(collectionToken: string): string {
  const params = new URLSearchParams({ [LIBRARY_COLLECTION_SCOPE_PARAM]: collectionToken })
  return `${ROUTES.home}?${params.toString()}`
}

/** Home with the composer scoped to one Library document, ready to ask. */
export function askDocumentPath(documentToken: string): string {
  const params = new URLSearchParams({ doc: documentToken })
  return `${ROUTES.home}?${params.toString()}`
}

/**
 * The scope an ask link names, or null when it names none. A Library
 * collection reads only its items, web off. A Topic (`topic`) keeps Auto with
 * the library and the web on and rides as a focus note (D0). A doc or a
 * knowledge collection (a social tracker's) is an explicit pick; a tracker
 * keeps the web out, and `source=social` / `window` narrow it to social posts
 * in that window.
 */
export function askScopeFromParams(params: URLSearchParams): KnowledgeScopeSelection | null {
  const doc = params.get('doc')
  const collection = params.get('collection')
  const libraryCollection = params.get(LIBRARY_COLLECTION_SCOPE_PARAM)?.trim()
  if (libraryCollection) {
    return {
      sourceMode: 'manual',
      web: false,
      library: true,
      mode: 'selected',
      documentTokens: [],
      collectionTokens: [],
      libraryCollectionTokens: [libraryCollection],
    }
  }
  const socialFocus = socialFocusFromParams(params)
  if (socialFocus) {
    return {
      sourceMode: 'auto',
      web: true,
      library: true,
      mode: 'all',
      documentTokens: [],
      collectionTokens: [],
      socialFocus,
    }
  }
  if (!doc && !collection) {
    return null
  }
  const isSocialSource = params.get('source') === 'social'
  const socialWindow = isSocialSource ? socialWindowFromParam(params.get('window')) : null
  return {
    sourceMode: 'manual',
    web: !collection,
    library: true,
    mode: 'selected',
    documentTokens: doc ? [doc] : [],
    collectionTokens: collection ? [collection] : [],
    ...(isSocialSource ? { sourceKinds: ['social'] } : {}),
    ...(socialWindow ? { socialWindow } : {}),
  }
}

/** The same params with the ask-scope ones removed, so a reload does not re-seed. */
export function withoutAskScopeParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params)
  for (const key of ASK_SCOPE_PARAMS) {
    next.delete(key)
  }
  return next
}

/** The scope a new thread stores: source mode, social limits, the Topic focus and Library collections kept. */
export function persistableAskScope(scope: KnowledgeScopeSelection): KnowledgeScopeSelection {
  const web = scope.web !== false
  const library = scope.library !== false
  const sourceScope = {
    sourceMode: scope.sourceMode ?? 'auto',
    ...(scope.sourceKinds ? { sourceKinds: scope.sourceKinds } : {}),
    ...(scope.socialWindow ? { socialWindow: scope.socialWindow } : {}),
    ...(scope.socialFocus ? { socialFocus: scope.socialFocus } : {}),
  }
  if (!library) {
    return {
      web,
      library: false,
      ...sourceScope,
      mode: 'all',
      documentTokens: [],
      collectionTokens: [],
    }
  }
  const hasItems =
    scope.mode === 'selected' &&
    (scope.documentTokens.length > 0 || scope.collectionTokens.length > 0)
  // D4: Library collections scope the thread on their own; the server
  // resolves their members as the asking user.
  const libraryCollectionTokens = scope.libraryCollectionTokens ?? []
  const isCollectionScoped = libraryCollectionTokens.length > 0
  return {
    web,
    library: true,
    mode: hasItems || isCollectionScoped ? 'selected' : 'all',
    documentTokens: hasItems ? scope.documentTokens : [],
    collectionTokens: hasItems ? scope.collectionTokens : [],
    ...sourceScope,
    ...(isCollectionScoped ? { libraryCollectionTokens } : {}),
  }
}
